import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useExam } from "../context/ExamContext";
import {
  registerUser,
  registerBeginnerUser,
  getCourses,
} from "../api/client";
import AuthLayout, { Field, PasswordInput } from "./Authlayout";

const GENDER_OPTIONS = ["Male", "Female", "Other"];

const NAME_RE = /^[\p{L}][\p{L}\s.'-]*$/u;
const MOBILE_RE = /^\+?\d{10,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Backend accepts classes 5 to 12 only.
const CLASS_LEVELS = [5, 6, 7, 8, 9, 10, 11, 12];

const ACCOUNT_NOTES = {
  professional:
    "Full exam simulation with AI proctoring. A webcam is required.",
  beginner:
    "Practice and exams for school students in classes 5 to 12, on a subscription plan.",
};

const emptyForm = {
  fullName: "",
  mobile: "",
  email: "",
  gender: "",
  courses: [],
  schoolClass: "",
  password: "",
  confirmPassword: "",
  consent: false,
};

const FIELD_ORDER = [
  "fullName",
  "mobile",
  "email",
  "gender",
  "courses",
  "schoolClass",
  "password",
  "confirmPassword",
  "consent",
];

const passwordRules = (p) => ({
  len: p.length >= 8,
  up: /[A-Z]/.test(p),
  lo: /[a-z]/.test(p),
  num: /\d/.test(p),
});

function validate(form, accountType) {
  const errors = {};
  const pro = accountType === "professional";

  const name = form.fullName.trim().replace(/\s+/g, " ");
  const mobile = form.mobile.replace(/[\s-]/g, "").trim();
  const email = form.email.trim();

  if (!name) errors.fullName = "Enter your full name.";
  else if (name.length < 3)
    errors.fullName = "Name must be at least 3 characters.";
  else if (name.length > 60)
    errors.fullName = "Name must be 60 characters or fewer.";
  else if (!NAME_RE.test(name))
    errors.fullName = "Name can only contain letters, spaces, . ' and -.";
  else if (!/\p{L}.*\p{L}/u.test(name))
    errors.fullName = "Enter a valid name.";

  if (!mobile) errors.mobile = "Enter your mobile number.";
  else if (!MOBILE_RE.test(mobile))
    errors.mobile = "Enter a valid mobile number (10 to 15 digits).";

  if (!email) errors.email = "Enter your email address.";
  else if (email.length > 254)
    errors.email = "Email address is too long.";
  else if (!EMAIL_RE.test(email)) errors.email = "Enter a valid email address.";

  if (!GENDER_OPTIONS.includes(form.gender))
    errors.gender = "Select your gender.";

  if (pro && form.courses.length === 0)
    errors.courses = "Select at least one course.";

  if (!pro && !CLASS_LEVELS.includes(Number(form.schoolClass)))
    errors.schoolClass = "Select your class.";

  const r = passwordRules(form.password);
  if (!form.password) errors.password = "Enter a password.";
  else if (/\s/.test(form.password))
    errors.password = "Password cannot contain spaces.";
  else if (form.password.length > 64)
    errors.password = "Password must be 64 characters or fewer.";
  else if (!(r.len && r.up && r.lo && r.num))
    errors.password =
      "Use at least 8 characters with uppercase, lowercase and a number.";

  if (!form.confirmPassword)
    errors.confirmPassword = "Confirm your password.";
  else if (form.confirmPassword !== form.password)
    errors.confirmPassword = "Passwords do not match.";

  if (pro && !form.consent)
    errors.consent = "Agree to proctoring to take professional exams.";

  return errors;
}

function formatApiError(error, fallback) {
  const data = error?.response?.data;

  if (typeof data === "string") return data;

  if (data && typeof data === "object") {
    return Object.entries(data)
      .map(([field, value]) => {
        const text = Array.isArray(value) ? value.join(" ") : String(value);
        return `${field}: ${text}`;
      })
      .join(" ");
  }

  if (error?.request) {
    return "Cannot connect to Django. Make sure the backend is running.";
  }

  return error?.message || fallback;
}

export default function Signup() {
  const navigate = useNavigate();
  const { setCurrentUser } = useExam();

  const [accountType, setAccountType] = useState("professional");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState("");

  const isPro = accountType === "professional";
  const rules = passwordRules(form.password);

  /* ---------- Load courses ---------- */
  useEffect(() => {
    let cancelled = false;

    async function loadCourses() {
      setCoursesLoading(true);
      setCoursesError("");

      try {
        const response = await getCourses();
        if (cancelled) return;

        const data = response?.data;
        setCourses(
          Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
              ? data.results
              : []
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load courses:", error);
        setCoursesError(
          error?.response?.data?.detail ||
            "Unable to load courses. Please try again."
        );
      } finally {
        if (!cancelled) setCoursesLoading(false);
      }
    }

    loadCourses();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------- Handlers ---------- */
  const clearError = (name) =>
    setErrors((current) => ({ ...current, [name]: undefined }));

  const handleChange = (event) => {
    const { name, value } = event.target;
    const next = name === "mobile" ? value.replace(/[^\d+\s-]/g, "") : value;

    setForm((current) => ({ ...current, [name]: next }));
    clearError(name);
  };

  // Validate a single field when the user leaves it (only if it has a value).
  const validateOnBlur = (name, value, overrideForm) => {
    if (!value) return;
    const found = validate(overrideForm || form, accountType);
    setErrors((current) => ({ ...current, [name]: found[name] }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateOnBlur(name, value);
  };

  const handleConsent = (event) => {
    setForm((current) => ({ ...current, consent: event.target.checked }));
    clearError("consent");
  };

  const handleCourseToggle = (id) => {
    setForm((current) => {
      const exists = current.courses.some((c) => Number(c) === Number(id));
      return {
        ...current,
        courses: exists
          ? current.courses.filter((c) => Number(c) !== Number(id))
          : [...current.courses, id],
      };
    });
    clearError("courses");
  };

  const switchAccountType = (type) => {
    setAccountType(type);
    setErrors({});
    setMessage({ type: "", text: "" });
  };

  const focusField = (field) => {
    const el = document.getElementById(`su-${field}`);
    if (!el) return;
    (el.matches("input,select") ? el : el.querySelector("input"))?.focus();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage({ type: "", text: "" });

    const found = validate(form, accountType);
    setErrors(found);

    const firstInvalid = FIELD_ORDER.find((field) => found[field]);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    const fullName = form.fullName.trim().replace(/\s+/g, " ");
    const mobile = form.mobile.replace(/[\s-]/g, "").trim();
    const email = form.email.trim();

    setBusy(true);

    /* ----- Professional ----- */
    if (isPro) {
      try {
        const response = await registerUser({
          full_name: fullName,
          mobile,
          email,
          gender: form.gender.toUpperCase(),
          course_ids: form.courses.map(Number),
          password: form.password,
          password_confirm: form.confirmPassword,
        });

        const data = response?.data || {};

        setCurrentUser({
          id: data.id || data.student_id || null,
          student_id: data.student_id || data.id || null,
          email: data.email || email,
          full_name: data.full_name || fullName,
          course_ids: data.course_ids || form.courses,
        });

        navigate("/login");
      } catch (error) {
        console.error("Professional signup error:", error);
        setMessage({
          type: "error",
          text: formatApiError(
            error,
            "Registration failed. Please try again."
          ),
        });
      } finally {
        setBusy(false);
      }
      return;
    }

    /* ----- Beginner ----- */
    try {
      const response = await registerBeginnerUser({
        full_name: fullName,
        mobile,
        email,
        class_level: Number(form.schoolClass),
        password: form.password,
        password_confirm: form.confirmPassword,
      });

      const data = response?.data || {};

      setMessage({
        type: "success",
        text: `${data.message || "Beginner registration successful."} Student ID: ${data.student_id || "created"}. You can now log in.`,
      });
      setForm(emptyForm);
    } catch (error) {
      console.error("Beginner signup error:", error);
      setMessage({
        type: "error",
        text: formatApiError(
          error,
          "Beginner registration failed. Please check your details."
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Render ---------- */
  return (
    <AuthLayout active="signup">
      <form onSubmit={handleSubmit} noValidate>
        <h2>Create your account</h2>
        <p className="sub">Choose the type of account you want to create.</p>

        <div className="type" role="group" aria-label="Account type">
          <button
            type="button"
            aria-pressed={isPro}
            onClick={() => switchAccountType("professional")}
          >
            <b>Professional</b>
            <span>Timed, proctored mocks</span>
          </button>
          <button
            type="button"
            aria-pressed={!isPro}
            onClick={() => switchAccountType("beginner")}
          >
            <b>Beginner</b>
            <span>School students, classes 5 to 12</span>
          </button>
        </div>

        <p
          className="sub"
          style={{ margin: "8px 0 18px", fontSize: 14 }}
        >
          {ACCOUNT_NOTES[accountType]}
        </p>

        <Field id="su-fullName" label="Full name" error={errors.fullName}>
          <input
            id="su-fullName"
            name="fullName"
            type="text"
            value={form.fullName}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="name"
            maxLength={60}
            placeholder="Enter your full name"
            aria-invalid={!!errors.fullName}
          />
        </Field>

        <div className="two">
          <Field id="su-mobile" label="Mobile number" error={errors.mobile}>
            <input
              id="su-mobile"
              name="mobile"
              type="tel"
              inputMode="tel"
              value={form.mobile}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="tel"
              maxLength={16}
              placeholder="e.g. 9876543210"
              aria-invalid={!!errors.mobile}
            />
          </Field>

          <Field id="su-email" label="Email" error={errors.email}>
            <input
              id="su-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleBlur}
              autoComplete="email"
              maxLength={254}
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
            />
          </Field>

          <Field id="su-gender" label="Gender" error={errors.gender}>
            <select
              id="su-gender"
              name="gender"
              value={form.gender}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!errors.gender}
            >
              <option value="">Select gender</option>
              {GENDER_OPTIONS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>

          {isPro ? (
            <div className={`f${errors.courses ? " bad" : ""}`}>
              <span className="lab">Course(s)</span>

              <div className="courses" id="su-courses">
                {coursesLoading && <div className="note">Loading courses...</div>}

                {!coursesLoading && coursesError && (
                  <div className="note" style={{ color: "var(--err)" }}>
                    {coursesError}
                  </div>
                )}

                {!coursesLoading && !coursesError && courses.length === 0 && (
                  <div className="note">
                    No courses are currently available.
                  </div>
                )}

                {!coursesLoading &&
                  !coursesError &&
                  courses.map((course) => {
                    const id = course.id;
                    const name = course.name || course.title || `Course ${id}`;

                    return (
                      <label className="chk" key={id}>
                        <input
                          type="checkbox"
                          checked={form.courses.some(
                            (c) => Number(c) === Number(id)
                          )}
                          onChange={() => handleCourseToggle(id)}
                        />{" "}
                        {name}
                      </label>
                    );
                  })}
              </div>

              <div className="err" role={errors.courses ? "alert" : undefined}>
                {errors.courses}
              </div>
            </div>
          ) : (
            <Field id="su-schoolClass" label="Class" error={errors.schoolClass}>
              <select
                id="su-schoolClass"
                name="schoolClass"
                value={form.schoolClass}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={!!errors.schoolClass}
              >
                <option value="">Select class</option>
                {CLASS_LEVELS.map((grade) => (
                  <option key={grade} value={grade}>
                    Class {grade}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>

        <Field
          id="su-password"
          label="Password"
          error={errors.password}
          hint={
            <ul className="rules" id="rules">
              <li className={rules.len ? "ok" : ""}>8+ characters</li>
              <li className={rules.up ? "ok" : ""}>Uppercase</li>
              <li className={rules.lo ? "ok" : ""}>Lowercase</li>
              <li className={rules.num ? "ok" : ""}>Number</li>
            </ul>
          }
        >
          <PasswordInput
            id="su-password"
            value={form.password}
            onChange={(e) => {
              const value = e.target.value;
              setForm((c) => ({ ...c, password: value }));
              clearError("password");
              // Keep the confirm-password error in sync once it has been typed.
              if (form.confirmPassword) {
                setErrors((c) => ({
                  ...c,
                  confirmPassword:
                    form.confirmPassword !== value
                      ? "Passwords do not match."
                      : undefined,
                }));
              }
            }}
            onBlur={() => validateOnBlur("password", form.password)}
            autoComplete="new-password"
            maxLength={64}
            placeholder="Create a password"
          />
        </Field>

        <Field
          id="su-confirmPassword"
          label="Confirm password"
          error={errors.confirmPassword}
        >
          <PasswordInput
            id="su-confirmPassword"
            value={form.confirmPassword}
            onChange={(e) => {
              setForm((c) => ({ ...c, confirmPassword: e.target.value }));
              clearError("confirmPassword");
            }}
            onBlur={() =>
              validateOnBlur("confirmPassword", form.confirmPassword)
            }
            autoComplete="new-password"
            maxLength={64}
            placeholder="Re-enter your password"
          />
        </Field>

        {isPro && (
          <Field id="su-consent" error={errors.consent}>
            <label className="chk">
              <input
                id="su-consent"
                type="checkbox"
                checked={form.consent}
                onChange={handleConsent}
              />{" "}
              I agree to AI proctoring (camera and screen) during exams
            </label>
          </Field>
        )}

        <button
          className="btn"
          type="submit"
          disabled={busy || (isPro && (coursesLoading || courses.length === 0))}
        >
          {busy ? "Signing up..." : "Sign up"}
        </button>

        {message.text && (
          <div
            className={`msg ${message.type}`}
            role={message.type === "error" ? "alert" : "status"}
          >
            {message.text}
            {message.type === "success" && (
              <>
                {" "}
                <Link to="/login">Go to Login</Link>
              </>
            )}
          </div>
        )}
      </form>
    </AuthLayout>
  );
}